import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import classNames from 'classnames';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import InviteLeadsFromPreviousDeals from 'components/InviteLeadsFromPreviousDeals';
import MakeNewLeadModal from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/subrouters/ListingInformation/components/MakeNewLeadModal';
import { DealRoom } from 'models/DealRoom';
import { checkIsPrivate } from 'utils';
import ActionListModal from 'components/ActionListModal';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import AddProfileIcon from 'icons/AddProfile.icon';
import { COLOR_VARIANT } from 'models/Colors';

import './AddLeadModal.styles.scss';

type AddLeadModalProps = {
  className?: string;
  dealRoom: Partial<DealRoom>;
  onInvitePrivate: () => void;
} & ModalProps;

const AddLeadModal: React.ForwardRefRenderFunction<
  ModalRef,
  AddLeadModalProps
> = (props, ref) => {
  const { className, dealRoom, onInvitePrivate, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const [newModal, newModalRef] = useCallbackRef<ModalRef>();
  const [selectModal, selectModalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { listingStage } = dealRoom;

  const classes = classNames('add-lead-modal', className);

  const isPrivate = checkIsPrivate(listingStage);

  const { t } = useTranslation();

  const items = useMemo(
    () => [
      {
        title: t('chooseLeadsFromDeal'),
        description: t('chooseLeadsFromDealDescription'),
        Icon: GroupUsersIcon,
        onClick: () => selectModal?.open(),
      },
      {
        title: t('inviteNewLead'),
        description: t('inviteNewLeadDescription'),
        color: COLOR_VARIANT.ORANGE,
        Icon: AddProfileIcon,
        onClick: () => newModal?.open(),
      },
    ],
    [newModal, selectModal, t],
  );

  return (
    <>
      <ActionListModal
        ref={modalRef}
        className={classes}
        items={items}
        title={t('addLeads')}
        description={t('howWouldYoulikeToAddLeads')}
        {...rest}
      />
      <InviteLeadsFromPreviousDeals ref={selectModalRef} />
      <MakeNewLeadModal
        ref={newModalRef}
        isPrivate={isPrivate}
        onInvitePrivate={onInvitePrivate}
      />
    </>
  );
};

export default forwardRef(AddLeadModal);
